<template>
  <v-card
    outlined
    class="unsaved-list mb-6"
  >
    <template v-for="(group, gIdx) in monthGroups">
      <div :key="`month-${group.key}`">
        <div class="month-label px-4 py-2">
          {{ group.label }}
        </div>

        <div
          v-for="row in group.rows"
          :key="`day-${row.business_date}`"
          class="day-block px-4 py-3"
        >
          <!-- 営業日の見出し + クロージング導線 -->
          <div class="day-header d-flex align-center mb-2">
            <span class="day-date">{{ formatDayLabel(row.business_date) }}</span>
            <v-spacer />
            <v-btn
              color="primary"
              small
              :ripple="false"
              @click="$emit('go-to-closing', row.business_date)"
            >
              この日のクロージングへ
              <v-icon right>
                {{ icons.mdiArrowRight }}
              </v-icon>
            </v-btn>
          </div>

          <!-- 状態カテゴリ（未集計 / 未保存 / 要再集計）ごと -->
          <div
            v-for="cat in categoryTree(row)"
            :key="`cat-${row.business_date}-${cat.category}`"
            class="cat-group"
          >
            <!-- カテゴリ見出し（クリックで開閉するプルダウン） -->
            <div
              class="cat-header"
              role="button"
              @click="toggleCat(row.business_date, cat.category)"
            >
              <v-icon
                small
                class="cat-chevron"
              >
                {{ isExpanded(row.business_date, cat.category) ? icons.mdiChevronDown : icons.mdiChevronRight }}
              </v-icon>
              <v-icon
                small
                :color="cat.color"
                class="mr-1"
              >
                {{ cat.icon }}
              </v-icon>
              <span
                class="cat-label"
                :class="`${cat.color}--text`"
              >{{ cat.label }}（{{ cat.count }}）</span>
              <span class="cat-desc">ー {{ cat.description }}</span>
            </div>

            <!-- 詳細（集計表タイプ > 集計表 > 対象）。開いたときのみ表示 -->
            <v-expand-transition>
              <div v-show="isExpanded(row.business_date, cat.category)">
                <div
                  v-for="type in cat.types"
                  :key="`type-${row.business_date}-${cat.category}-${type.type}`"
                  class="type-group"
                >
                  <div
                    v-if="type.typeLabel"
                    class="type-label"
                  >
                    {{ type.typeLabel }}
                  </div>

                  <!-- 集計表ごと（その下に対象を並べる） -->
                  <div
                    v-for="agg in type.aggregations"
                    :key="`agg-${row.business_date}-${cat.category}-${type.type}-${agg.name}`"
                    class="agg-group"
                  >
                    <div
                      v-if="agg.name"
                      class="agg-name"
                    >
                      ・{{ agg.name }}
                    </div>
                    <div
                      v-for="(target, tIdx) in agg.targets"
                      :key="`tgt-${tIdx}`"
                      class="target-item"
                    >
                      ・{{ target.name }}
                      <v-chip
                        v-if="cat.category === assignableCategory && target.id != null"
                        x-small
                        color="primary"
                        class="ml-1 assign-chip"
                        @click.stop="$emit('assign-user', target)"
                      >
                        集計表に登録
                      </v-chip>
                    </div>
                  </div>
                </div>
              </div>
            </v-expand-transition>
          </div>
        </div>
      </div>

      <v-divider
        v-if="gIdx < monthGroups.length - 1"
        :key="`month-divider-${group.key}`"
      />
    </template>
  </v-card>
</template>

<script>
import { computed, ref } from '@vue/composition-api'
import {
  parseISO, getYear, getMonth, getDate, getDay,
} from 'date-fns'
import {
  mdiArrowRight,
  mdiAlertCircle,
  mdiAccountAlert,
  mdiCircle,
  mdiSyncAlert,
  mdiChevronRight,
  mdiChevronDown,
} from '@mdi/js'

// 曜日の日本語表記（date-fns の ja ロケールに依存しないための定数）
const WEEKDAY_JA = ['日', '月', '火', '水', '木', '金', '土']

// 診断カテゴリ → 表示メタ。
// バックエンドの finding.category 値:
//   aggregation_job_missing          ... 未集計（集計ジョブが実行されていない）
//   result_missing_after_job         ... 未保存（集計は完了したが結果の一部が未保存）
//   timecard_updated_after_job       ... 要再集計（集計後にタイムカードが変更された）
//   attended_user_not_in_aggregation ... 集計表未所属（出勤があるのに集計対象になっていない）
const CATEGORY_META = {
  aggregation_job_missing: {
    label: '未集計',
    color: 'error',
    icon: mdiAlertCircle,
    description: '集計が実行されていません',
  },
  result_missing_after_job: {
    label: '未保存',
    color: 'warning',
    icon: mdiCircle,
    description: '集計は完了したが集計結果の一部が未保存',
  },
  timecard_updated_after_job: {
    label: '要再集計',
    color: 'warning',
    icon: mdiSyncAlert,
    description: '集計後にタイムカードが変更されました',
  },
  attended_user_not_in_aggregation: {
    label: '集計表未所属',
    color: 'error',
    icon: mdiAccountAlert,
    description: '出勤していますが、どの集計表の対象にもなっていません',
  },
}

// 「集計表に登録」チップでその場登録できるカテゴリ
const ASSIGNABLE_CATEGORY = 'attended_user_not_in_aggregation'

const FALLBACK_CATEGORY = {
  label: '要確認',
  color: 'warning',
  icon: mdiCircle,
  description: '集計結果に不整合があります',
}

// 卓集計の context_type はバックエンドが 'TableDetail'（base_class 名）を返す。
// 'Table' をキーにすると見出しが生文字列のまま表示され、並び順も崩れる。
const CONTEXT_TYPE_LABELS = {
  User: 'ユーザー集計',
  TableDetail: '卓集計',
  Club: '店舗集計',
}

// 表示順（既知のものを先に、未知のものは後ろに）
const CATEGORY_ORDER = [
  'aggregation_job_missing',
  'result_missing_after_job',
  'timecard_updated_after_job',
  'attended_user_not_in_aggregation',
]
const TYPE_ORDER = ['User', 'TableDetail', 'Club']

export default {
  name: 'UnsavedList',
  props: {
    // 診断APIの営業日ごとの rows
    rows: {
      type: Array,
      default: () => [],
    },
    // 当月判定の基準日 (YYYY-MM-DD)
    today: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    // カテゴリ（営業日 × 状態カテゴリ）ごとの開閉状態。既定は閉じる。
    const expanded = ref({})
    const expandKey = (businessDate, category) => `${businessDate}-${category}`
    const isExpanded = (businessDate, category) => (
      !!expanded.value[expandKey(businessDate, category)]
    )
    const toggleCat = (businessDate, category) => {
      const key = expandKey(businessDate, category)
      expanded.value = { ...expanded.value, [key]: !expanded.value[key] }
    }

    const categoryMeta = key => CATEGORY_META[key] || FALLBACK_CATEGORY

    const contextTypeLabel = type => CONTEXT_TYPE_LABELS[type] || type || ''

    const formatDayLabel = dateStr => {
      try {
        const d = parseISO(dateStr)

        return `${getMonth(d) + 1}/${getDate(d)}（${WEEKDAY_JA[getDay(d)]}）`
      } catch (e) {
        return dateStr
      }
    }

    // row.findings を「状態カテゴリ > 集計表タイプ > 集計表 > 対象」の
    // 入れ子ツリーに束ねる。finding は BR 単位のため、同じ対象（ユーザー等）が
    // 未保存 BR の数だけ重複する。context_id（無ければ context_name）をキーに
    // 重複排除し、対象を「ユーザー単位」に束ねる。
    const categoryTree = row => {
      const findings = row.findings || []

      // catMap[cat][type][aggName] は { ctxKey: context_name } のマップ。
      const catMap = {}
      findings.forEach(f => {
        const cat = f.category || 'unknown'
        const type = f.context_type || ''
        const aggName = f.aggregation_name || ''
        if (!catMap[cat]) catMap[cat] = {}
        if (!catMap[cat][type]) catMap[cat][type] = {}
        if (!catMap[cat][type][aggName]) catMap[cat][type][aggName] = {}
        if (f.context_name) {
          const ctxKey = f.context_id != null ? `id:${f.context_id}` : `name:${f.context_name}`
          catMap[cat][type][aggName][ctxKey] = { id: f.context_id, name: f.context_name }
        }
      })

      const orderedCats = [
        ...CATEGORY_ORDER.filter(c => catMap[c]),
        ...Object.keys(catMap).filter(c => !CATEGORY_ORDER.includes(c)),
      ]

      return orderedCats.map(cat => {
        const typeMap = catMap[cat]
        const orderedTypes = [
          ...TYPE_ORDER.filter(t => typeMap[t]),
          ...Object.keys(typeMap).filter(t => !TYPE_ORDER.includes(t)),
        ]
        const types = orderedTypes.map(type => ({
          type,
          typeLabel: contextTypeLabel(type),
          aggregations: Object.keys(typeMap[type]).map(name => ({
            name,
            targets: Object.values(typeMap[type][name]),
          })),
        }))

        // 件数 = カテゴリ内の対象（ユーザー等）の総数。
        // 対象が紐づかない集計表は 1 件として数える。
        let count = 0
        types.forEach(t => {
          t.aggregations.forEach(agg => {
            count += agg.targets.length > 0 ? agg.targets.length : 1
          })
        })

        return {
          category: cat,
          ...categoryMeta(cat),
          count,
          types,
        }
      })
    }

    // 当月（基準日と同年同月）/ 前月の2グループに分割する。
    const monthGroups = computed(() => {
      const baseDate = props.today ? parseISO(props.today) : new Date()
      const baseYear = getYear(baseDate)
      const baseMonth = getMonth(baseDate)

      const currentRows = []
      const previousRows = [];
      [...props.rows]
        .sort((a, b) => (a.business_date < b.business_date ? 1 : -1))
        .forEach(row => {
          let rowDate
          try {
            rowDate = parseISO(row.business_date)
          } catch (e) {
            rowDate = null
          }
          if (rowDate && getYear(rowDate) === baseYear && getMonth(rowDate) === baseMonth) {
            currentRows.push(row)
          } else {
            previousRows.push(row)
          }
        })

      const monthLabel = date => `${getYear(date)}年${getMonth(date) + 1}月`
      const prevDate = new Date(baseYear, baseMonth - 1, 1)

      const groups = []
      if (currentRows.length > 0) {
        groups.push({ key: 'current', label: `当月（${monthLabel(baseDate)}）`, rows: currentRows })
      }
      if (previousRows.length > 0) {
        groups.push({ key: 'previous', label: `前月（${monthLabel(prevDate)}）`, rows: previousRows })
      }

      return groups
    })

    return {
      monthGroups,
      categoryTree,
      formatDayLabel,
      isExpanded,
      toggleCat,
      assignableCategory: ASSIGNABLE_CATEGORY,
      icons: {
        mdiArrowRight,
        mdiChevronRight,
        mdiChevronDown,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.month-label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .5px;
  opacity: .6;
  border-bottom: 1px solid rgba(0, 0, 0, .08);
}

.day-block {
  border-bottom: 1px solid rgba(0, 0, 0, .08);

  &:last-child {
    border-bottom: none;
  }
}

.day-date {
  font-size: 15px;
  font-weight: 700;
}

// 状態カテゴリのまとまり
.cat-group {
  margin-top: 10px;

  &:first-of-type {
    margin-top: 4px;
  }
}

.cat-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-size: 13px;
  cursor: pointer;
  user-select: none;
  padding: 2px 0;

  &:hover {
    opacity: .85;
  }
}

.cat-chevron {
  margin-right: 2px;
}

.cat-label {
  font-weight: 700;
}

.cat-desc {
  margin-left: 4px;
  font-size: 12px;
  opacity: .7;
}

// 集計表タイプ（ユーザー集計 / 卓集計 / 店舗集計）
.type-group {
  margin-top: 4px;
  margin-left: 14px;
}

.type-label {
  font-size: 13px;
  font-weight: 700;
  opacity: .85;
}

// 集計表名
.agg-group {
  margin-left: 14px;
}

.agg-name {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.7;
}

// 対象（ユーザー名・卓名など）
.target-item {
  margin-left: 16px;
  font-size: 12px;
  line-height: 1.6;
  opacity: .75;
}

// 「集計表に登録」チップ
.assign-chip {
  cursor: pointer;
}
</style>
